import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { scrollToSection } from '../lib/sectionNavigation';

const plans = [
  {
    name: 'Trial',
    tagline: 'Get started risk-free',
    users: '1 User',
    isTrial: true,
    features: [
      'Single user access',
      'AAS editor with basic use cases',
      'Usage of the Validation Service (no API)',
      'Usage of the official IDTA Submodel Template Repository',
      'Self-service documentation access',
      'Data consumer only',
    ],
  },
  {
    name: 'Basic',
    tagline: 'For starters',
    users: '1 User',
    features: [
      'Single user access',
      'Registration of a server to the ADX Hub',
      'AAS editor with full features',
      'API access to the Validation Service',
      'API access to the official IDTA Submodel Template Repository',
      'Standard support (business hours)',
    ],
  },
  {
    name: 'Business',
    tagline: 'For teams',
    users: '5 Users',
    features: [
      'Access for up to five users',
      'Registration of several servers to the ADX Hub',
      'AAS editor with full features',
      'API access to the Validation Engine',
      "Validation check available, including the official 'AAS enabled' badge",
      'API access to the official IDTA Submodel Template Repository',
      'Priority support',
    ],
  },
  {
    name: 'Enterprise',
    tagline: 'For enterprises',
    users: '10 Users',
    note: 'For user plans >10 please contact us',
    features: [
      'Access for up to ten users*',
      'Registration of several servers to the ADX Hub',
      'AAS editor with full features',
      'API access to the Validation Service',
      "Validation check available, including the official 'AAS enabled' badge",
      'API access to the official IDTA Submodel Template Repository',
      'Dedicated enterprise support',
    ],
  },
];

const Pricing = () => {
  const handlePricingAction = (plan, isTrial) => {
    const params = new URLSearchParams(window.location.search);
    params.set('topic', isTrial ? 'free-trial' : 'contact-sales');
    params.set('source', isTrial ? 'pricing-trial' : 'pricing');
    params.set('plan', plan);

    const nextUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', nextUrl);
    scrollToSection('contact-form');
  };

  return (
    <section id="pricing" className="bg-[#f8fafc] py-16 md:py-20" data-testid="pricing-section">
      <div className="mx-auto max-w-[1420px] px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.4 }}
          className="mx-auto max-w-2xl text-center"
        >
          <h2 className="text-3xl font-bold tracking-tight text-[#0028CD] md:text-4xl">
            Pricing
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-slate-500">
            ADX Hub packages are quote-based. Contact sales to receive pricing tailored to your onboarding scope.
          </p>
        </motion.div>

        <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
          {plans.map((plan, index) => {
            return (
              <motion.article
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.38, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`plan-${plan.name.toLowerCase()}`}
                className="flex h-full flex-col rounded-3xl border border-[#0028CD]/20 bg-white p-6 text-[#0f172a] shadow-[0_12px_30px_rgba(2,36,128,0.07)] transition-all duration-200 hover:-translate-y-0.5 hover:border-[#0028CD]/45"
              >
                <div className="min-h-[104px]">
                  <p className="text-lg font-semibold uppercase tracking-tight text-[#0028CD]">
                    {plan.name}
                  </p>
                  <p className="mt-3 text-sm text-[#0028CD]/70">{plan.tagline}</p>
                  {plan.note ? (
                    <p className="mt-2 max-w-[13rem] text-xs leading-snug text-slate-500">
                      {plan.note}
                    </p>
                  ) : null}
                </div>

                <div className="mt-2 inline-flex w-fit items-center rounded-full border border-[#0028CD]/20 px-3 py-1 text-[12px] font-semibold text-[#0028CD]">
                  {plan.users}
                </div>

                <div className="mt-5 border-t border-[#0028CD]/15 pt-5">
                  <p className="max-w-[18ch] text-[22px] font-bold leading-tight tracking-tight text-[#0028CD] md:text-[24px]">
                    {plan.isTrial ? 'Free trial available' : 'Pricing available upon request'}
                  </p>
                  {plan.isTrial ? (
                    <p className="mt-2 text-sm text-slate-600">30 days free of charge</p>
                  ) : (
                    <>
                      <p className="mt-2 text-sm text-slate-600">Custom annual package</p>
                      <p className="mt-3 inline-flex rounded-full bg-[#FF500F]/10 px-3 py-1 text-xs font-semibold text-[#FF500F]">
                        IDTA members are eligible for up to 33% discount
                      </p>
                    </>
                  )}
                </div>

                <div className="mt-5 flex-1 border-t border-[#0028CD]/15 pt-5">
                  <ul className="space-y-3">
                    {plan.features.map((feature) => (
                      <li
                        key={feature}
                        className="flex items-start gap-2.5 text-[13.5px] leading-[1.42] text-slate-700"
                      >
                        <CheckCircle2
                          className="mt-[1px] h-4 w-4 shrink-0 text-[#0028CD]"
                          strokeWidth={2.1}
                        />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  type="button"
                  onClick={() => handlePricingAction(plan.name, Boolean(plan.isTrial))}
                  className="mt-6 inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl border-2 border-[#0028CD] bg-white px-5 text-xs font-bold text-[#0028CD] transition-all duration-200 hover:-translate-y-0.5 hover:border-[#FF500F] hover:shadow-[0_6px_20px_rgba(0,40,205,0.2)]"
                >
                  {plan.isTrial ? 'Start free trial' : 'Contact sales'}
                  <ArrowRight size={14} />
                </button>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
